import axiosClient from "./apiClient";

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

// Pull the req_id the API returns from a command POST/GET (UpdateScheduler,
// GetSchedule, GetDeviceStatus, …). Used to address the MQTT reply poll.
export function extractReqId(data: unknown): string | null {
  if (data && typeof data === "object") {
    const id = (data as Record<string, unknown>).req_id;
    if (typeof id === "string" && id.length > 0) return id;
  }
  return null;
}

// One-shot fetch of the MQTT events recorded for a req_id. Returns an empty
// array when the device hasn't responded yet.
export async function getMqttEvents(
  name: string,
  reqId: string
): Promise<unknown[]> {
  const res = await axiosClient.get(`sensors/${name}/GetMqttEvents/${reqId}`);
  const body = res.data;
  if (Array.isArray(body)) return body;
  return [];
}

export type PollOptions = {
  intervalMs?: number;
  timeoutMs?: number;
  maxAttempts?: number;
  readyWhen?: (responses: unknown[]) => boolean;
};

// Poll GetMqttEvents until a device *reply* arrives (and, when readyWhen is
// supplied, carries the expected payload) or the deadline/attempt cap is hit.
// The endpoint echoes the outbound request as direction:"req"; we ignore those.
export async function pollMqttEvents(
  name: string,
  reqId: string,
  options: PollOptions = {}
): Promise<unknown[]> {
  const intervalMs = options.intervalMs ?? 2000;
  const timeoutMs = options.timeoutMs ?? 30000;
  const maxAttempts = options.maxAttempts;
  const deadline = Date.now() + timeoutMs;
  let attempts = 0;

  while (true) {
    attempts++;
    const all = await getMqttEvents(name, reqId);
    const responses = all.filter((e) => {
      if (e && typeof e === "object") {
        return (e as Record<string, unknown>).direction !== "req";
      }
      return true;
    });
    const ready =
      responses.length > 0 &&
      (!options.readyWhen || options.readyWhen(responses));
    if (ready) return responses;

    if (Date.now() >= deadline) {
      throw new Error(
        `No device response for req_id=${reqId} after ${attempts} attempt(s).`
      );
    }
    if (maxAttempts != null && attempts >= maxAttempts) {
      throw new Error(
        `No device response for req_id=${reqId} after ${attempts} attempt(s).`
      );
    }
    await delay(intervalMs);
  }
}
